import React from 'react';
import { useIsFetching, useMutation, useQueryClient } from '@tanstack/react-query';
import pt from 'prop-types';
import useBackgrounds from '../../../hooks/useBackgrounds';
import useImageTypes from '../../../hooks/useImageTypes';
import usePoses from '../../../hooks/usePoses';
import './scrollbar.scss';
import Builder from './Builder';
import Sheet from '../../../models/Sheet';
import useOption from '../../../hooks/useOption';
import QUERY_KEYS from '../../../utils/QueryKeys';
import usePackagePoses from '../../../hooks/usePackagePoses';
import usePricelist from '../../../hooks/usePricelist';
import useMultiSeasonPoses from "../../../hooks/useMultiSeasonPoses";

export default function MultiPose({
  cartId,
  optionId,
  packageId,
  schoolId,
  studentId,
  orderPackage,
  onSave,
  pastImages,
  favoriteIds,
  sheetId,
  preorderPose
}) {
  // API Data
  const { data: option } = useOption(optionId, schoolId);
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: imageTypes } = useImageTypes(option && option.id);
  const { data: packagePoses } = usePackagePoses(packageId);
  const { data: pricelist } = usePricelist(packageId);
  const { data: poses } = pricelist?.id && (pricelist?.multipleSeasons?.length || pricelist?.displaySharedPoses) ? (
    useMultiSeasonPoses(studentId, packageId)
  ) : (
    usePoses(studentId, packageId, pastImages)
  )
  const queryClient = useQueryClient();

  const {
    mutate,
    isLoading,
  } = useMutation({
    mutationFn: async ({ sheets, additionalPoses }) => {
      // If we're editing a single sheet, grab the index of the previous value
      let prevIndex = 0;

      // Destroy any sheets on the orderPackage (if any), and replace with the new sheets
      const oldSheets = orderPackage.sheets.map((sheet) => {
        if (!sheetId) sheet.isMarkedForDestruction = true;
        if (sheetId && sheet.id.toString() === sheetId.toString()) {
          prevIndex = sheet.index;
          sheet.isMarkedForDestruction = true;
        }

        return sheet;
      });

      orderPackage.sheets = oldSheets;

      await orderPackage.save({ with: 'sheets' });

      const newSheets = sheets.map((sheetParams) => {
        if (sheetId) return new Sheet({ ...sheetParams, index: prevIndex });

        return new Sheet({ ...sheetParams });
      });

      orderPackage.cartId = cartId;
      orderPackage.studentId = studentId;
      orderPackage.optionId = option.id;
      orderPackage.packageId = packageId;
      orderPackage.apiOption = option;
      orderPackage.extraPoses = additionalPoses;
      orderPackage.sheets = [...newSheets];

      return orderPackage.save({ with: 'sheets' });
    },
    onSuccess: () => {
      if (Object.keys(orderPackage.errors).length > 0) return;

      // Invalidate cart queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cart, QUERY_KEYS.numItems] });
      
      // ADD THIS: Invalidate order package queries to refresh the DisplayableSheet data
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.orderPackage, orderPackage.id] });
      queryClient.invalidateQueries({ queryKey: ['orderPackage', orderPackage.id] });
      
      // If you have specific sheet queries, invalidate those too
      queryClient.invalidateQueries({ queryKey: ['sheets'] });
      queryClient.invalidateQueries({ queryKey: ['orderPackageSheets', orderPackage.id] });
      
      onSave();
    },
  });

  // UI State
  const isFetching = useIsFetching();

  if (isFetching) return null;
  if (!option) return null;

  const editableSheets = orderPackage
    .sheets
    .filter((sheet) => {
      if (sheetId) return sheet.id.toString() === sheetId.toString();

      return sheet;
    });

  return (
    <Builder
      isEditingSingleSheet={!!sheetId}
      backgrounds={backgrounds}
      favoriteIds={favoriteIds}
      isLoading={isLoading}
      initialSheets={editableSheets}
      errors={orderPackage.errors}
      imageTypes={imageTypes}
      packageId={packageId}
      packagePoses={packagePoses}
      poses={preorderPose ? [preorderPose] : poses}
      onConfirm={(sheets, additionalPoses) => mutate({ sheets, additionalPoses })}
      option={option}
    />
  );
}

MultiPose.defaultProps = {
  favoriteIds: [],
  pastImages: false,
};

MultiPose.propTypes = {
  cartId: pt.string.isRequired,
  optionId: pt.string.isRequired,
  packageId: pt.string.isRequired,
  schoolId: pt.string.isRequired,
  studentId: pt.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  orderPackage: pt.object.isRequired,
  onSave: pt.func.isRequired,
  pastImages: pt.bool,
  favoriteIds: pt.arrayOf(pt.string),
};
